/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import Tippy from "@tippyjs/react/headless";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import FocusTrap from "focus-trap-react";
import { Icon } from "../Icons/Icons";
import { InputWrapper } from "../InputWrapper";
import { renderReactChildrenWithData } from "../../utils";
import * as styles from "./PopoverInput.module.scss";

const PopoverInput = ({
	className,
	popoverClassName,
	inputClassName,
	id,
	label,
	icon,
	readOnly,
	value,
	children,
	onChange,
	onClose,
	disableLockScroll,
	defaultCloseIcon,
	theme,
}) => {
	const referenceElement = useRef(null);
	const [isReadOnlyReturnedFocus, setIsReadOnlyReturnedFocus] = useState(false);
	const [showPopover, setShowPopover] = useState(false);

	useEffect(() => {
		if (showPopover && referenceElement?.current) {
			// updateContainerSize();

			disableBodyScroll(referenceElement.current, {
				allowTouchMove: el => {
					let _el = el;
					while (_el && _el.parentElement) {
						if (_el.hasAttribute("body-scroll-lock-ignore")) {
							return true;
						}

						_el = _el.parentElement;
					}
					return false;
				},
			});
		}

		if (disableLockScroll) {
			clearAllBodyScrollLocks();
		}

		return () => {
			clearAllBodyScrollLocks();
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showPopover]);

	return (
		<InputWrapper
			ref={referenceElement}
			className={cx(
				className,
				styles.inputWrapper,
				showPopover && styles.popoverOpen,
				theme === "filters" && styles.filters
			)}
			label={label}
			theme={theme}
			id={id}
			onBlur={() => !showPopover}>
			{({
				// isFocused,
				handleOnFocus,
				handleOnBlur,
			}) => (
				<div
					className={cx(
						inputClassName,
						styles.inputContainer
						//  isFocused && styles.focused
					)}>
					{theme !== "filters" ? (
						<>
							<input
								className={cx(styles.input, icon && styles.inputWithIcon)}
								value={value}
								type="text"
								readOnly={readOnly}
								onChange={e => onChange(e.target.value)}
								onClick={() => {
									if (readOnly) {
										setIsReadOnlyReturnedFocus(true);
										setShowPopover(!showPopover);
										handleOnFocus();
									}
								}}
								onKeyDown={e => {
									if (readOnly) {
										if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
											e.preventDefault();
											setIsReadOnlyReturnedFocus(true);
											setShowPopover(!showPopover);
											handleOnFocus();
										}
									}
								}}
								onFocus={() => {
									handleOnFocus();
								}}
								onBlur={() => {
									handleOnBlur();
								}}
							/>
							{icon && (
								<button
									type="button"
									className={styles.icon}
									onClick={() => {
										setShowPopover(!showPopover);
										handleOnFocus();
									}}>
									{icon}
								</button>
							)}
						</>
					) : (
						<>
							<button
								type="button"
								className={styles.filtersInput}
								onClick={() => {
									setShowPopover(!showPopover);
									handleOnFocus();
								}}>
								<input
									className={cx(styles.input, icon && styles.inputWithIcon)}
									value={value}
									type="text"
									id={id}
									tabIndex={-1}
									readOnly={readOnly}
									onChange={e => onChange(e.target.value)}
									onClick={() => {
										if (readOnly) {
											setIsReadOnlyReturnedFocus(true);
											setShowPopover(!showPopover);
											handleOnFocus();
										}
									}}
									onKeyDown={e => {
										if (readOnly) {
											if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
												e.preventDefault();
												setIsReadOnlyReturnedFocus(true);
												setShowPopover(!showPopover);
												handleOnFocus();
											}
										}
									}}
									onFocus={() => {
										handleOnFocus();
									}}
									onBlur={() => {
										handleOnBlur();
									}}
								/>
								{icon && <span className={styles.icon}>{icon}</span>}
							</button>
							{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
							<label htmlFor={id} className={styles.srOnly}>
								Open price range filter
							</label>
						</>
					)}
					{showPopover && (
						<Tippy
							reference={referenceElement.current}
							visible={showPopover}
							interactive={true}
							appendTo={referenceElement.current}
							placement="bottom-start"
							popperOptions={{
								strategy: "absolute",
								modifiers: [
									{
										name: "flip",
										enabled: false,
									},
									{
										name: "offset",
										enabled: true,
										options: {
											offset: [0, 8],
										},
									},
									{
										name: "preventOverflow",
										enabled: true,
										options: {
											padding: 0,
											boundary: window !== undefined ? window : "scrollParent",
										},
									},
									{
										name: "maxHeight",
										enabled: true,
										phase: "afterMain",
										requiresIfExists: ["offset"],
										fn({ state }) {
											const height =
												window.innerHeight - state.elements.popper.getBoundingClientRect().top - 16;

											// eslint-disable-next-line no-param-reassign
											state.styles.popper = {
												...state.styles.popper,
												maxHeight: `${height}px`,
											};
										},
									},
								],
							}}
							render={attrs => {
								const handleClosePopover = () => {
									if (onClose()) {
										setShowPopover(false);
										if (readOnly && isReadOnlyReturnedFocus) {
											setIsReadOnlyReturnedFocus(false);
											handleOnFocus(true);
										} else {
											handleOnBlur(true);
										}
										return true;
									}
									return false;
								};

								return (
									<FocusTrap
										active={showPopover}
										paused={!showPopover}
										focusTrapOptions={{
											allowOutsideClick: e => {
												const canClose = onClose();
												// If clicked on same input
												if (e.target.closest("label")?.contains(referenceElement.current)) {
													return canClose;
												}

												if (!canClose) {
													e.stopPropagation();
												}

												return handleClosePopover();
											},
											onDeactivate: handleClosePopover,
										}}>
										<div
											// eslint-disable-next-line react/jsx-props-no-spreading
											{...attrs}
											className={cx(popoverClassName, styles.popover)}>
											<div className={cx(styles.container, showPopover && styles.open)}>
												{defaultCloseIcon && (
													<button
														type="button"
														className={styles.closeButton}
														onClick={handleClosePopover}>
														<Icon name="close--n" className={styles.closeIcon} />
													</button>
												)}
												{renderReactChildrenWithData(children, {
													isOpen: showPopover,
													handleClose: handleClosePopover,
													handleUpdateValue: _value => {
														onChange(_value);
														handleClosePopover();
													},
												})}
											</div>
										</div>
									</FocusTrap>
								);
							}}
						/>
					)}
				</div>
			)}
		</InputWrapper>
	);
};

PopoverInput.propTypes = {
	className: PropTypes.string,
	popoverClassName: PropTypes.string,
	inputClassName: PropTypes.string,
	id: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	icon: PropTypes.node,
	readOnly: PropTypes.bool,
	value: PropTypes.string.isRequired,
	children: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.arrayOf(PropTypes.node)])
		.isRequired,
	onChange: PropTypes.func.isRequired,
	onClose: PropTypes.func,
	disableLockScroll: PropTypes.bool,
	defaultCloseIcon: PropTypes.bool,
	theme: PropTypes.oneOf(["default", "filters"]),
};

PopoverInput.defaultProps = {
	className: undefined,
	inputClassName: undefined,
	popoverClassName: undefined,
	icon: undefined,
	readOnly: true,
	onClose: () => true,
	disableLockScroll: false,
	defaultCloseIcon: true,
	theme: "default",
};

export default PopoverInput;
export { PopoverInput };
